import React from 'react';
import { PRINT_COLORS } from '../printTheme';

const PrintSummaryStats = ({ dashboardSummary }) => {
  if (!dashboardSummary) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text }}>Summary Statistics</h3>
        <p style={{ color: PRINT_COLORS.text }}>No summary data available</p>
      </div>
    );
  }

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  };

  return (
    <div style={{ width: '100%', maxWidth: '750px', padding: '20px', background: PRINT_COLORS.background }}>
      <h2 style={{ 
        color: PRINT_COLORS.text, 
        marginBottom: '20px', 
        fontSize: '18px',
        fontWeight: 'bold',
        borderBottom: `2px solid ${PRINT_COLORS.border}`,
        paddingBottom: '10px'
      }}>
        Summary Statistics
      </h2>

      {/* Overview Section */}
      <div style={{ marginBottom: '25px' }}>
        <h3 style={{ 
          color: PRINT_COLORS.text, 
          fontSize: '14px', 
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Overall QAQC Status
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '10px',
          fontSize: '11px'
        }}>
          <div style={{ 
            padding: '10px', 
            background: PRINT_COLORS.backgroundLight,
            border: `1px solid ${PRINT_COLORS.border}`,
            borderRadius: '4px'
          }}>
            <div style={{ color: PRINT_COLORS.text, marginBottom: '5px' }}>Total Samples Analyzed</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: PRINT_COLORS.text }}>
              {formatNumber(dashboardSummary.total_samples)}
            </div>
          </div>
          <div style={{ 
            padding: '10px', 
            background: PRINT_COLORS.backgroundLight,
            border: `1px solid ${PRINT_COLORS.border}`,
            borderRadius: '4px'
          }}>
            <div style={{ color: PRINT_COLORS.text, marginBottom: '5px' }}>Unresolved Failures</div>
            <div style={{ 
              fontSize: '20px', 
              fontWeight: 'bold', 
              color: dashboardSummary.unresolved_failures > 0 ? PRINT_COLORS.fail : PRINT_COLORS.pass
            }}>
              {formatNumber(dashboardSummary.unresolved_failures)}
            </div>
          </div>
        </div>
      </div>

      {/* Control Standards Section */}
      <div style={{ marginBottom: '25px' }}>
        <h3 style={{ 
          color: PRINT_COLORS.text, 
          fontSize: '14px', 
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Control Standards (CRMs)
        </h3>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '10px'
        }}>
          <thead>
            <tr style={{ background: PRINT_COLORS.backgroundMedium }}>
              <th style={{ padding: '8px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Metric</th>
              <th style={{ padding: '8px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Total Standards Analyzed</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}` }}>
                {formatNumber(dashboardSummary.control_standards_count)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Within ±2SD (Pass)</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.pass }}>
                {formatNumber(dashboardSummary.control_standards_pass_2sd)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Outside ±3SD (Fail)</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.fail }}>
                {formatNumber(dashboardSummary.control_standards_fail_3sd)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Blanks Section */}
      <div style={{ marginBottom: '25px' }}>
        <h3 style={{ 
          color: PRINT_COLORS.text, 
          fontSize: '14px', 
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Blank Samples
        </h3>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '10px'
        }}>
          <thead>
            <tr style={{ background: PRINT_COLORS.backgroundMedium }}>
              <th style={{ padding: '8px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Metric</th>
              <th style={{ padding: '8px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Total Blanks Analyzed</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}` }}>
                {formatNumber(dashboardSummary.blank_samples_count)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Pass</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.pass }}>
                {formatNumber(dashboardSummary.blank_samples_pass)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Fail</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.fail }}>
                {formatNumber(dashboardSummary.blank_samples_fail)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Field Duplicates Section */}
      <div style={{ marginBottom: '25px' }}>
        <h3 style={{ 
          color: PRINT_COLORS.text, 
          fontSize: '14px', 
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Field Duplicates
        </h3>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '10px'
        }}>
          <thead>
            <tr style={{ background: PRINT_COLORS.backgroundMedium }}>
              <th style={{ padding: '8px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Metric</th>
              <th style={{ padding: '8px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Total Duplicates Analyzed</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}` }}>
                {formatNumber(dashboardSummary.field_duplicates_count)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Within Dynamic Tolerance (Pass)</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.pass }}>
                {formatNumber(dashboardSummary.field_duplicates_pass)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '6px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Outside Tolerance (Fail)</td>
              <td style={{ padding: '6px', textAlign: 'right', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.fail }}>
                {formatNumber(dashboardSummary.field_duplicates_fail)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ 
        marginTop: '12px',
        padding: '10px', 
        background: PRINT_COLORS.backgroundLight,
        border: `1px solid ${PRINT_COLORS.border}`,
        borderRadius: '4px',
        fontSize: '9px',
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        <strong>Note:</strong> Statistics are calculated from all available data within the specified date range. 
        CRM standards validate analytical accuracy, blanks detect contamination, and field duplicates assess precision.
      </div>
    </div>
  );
};

export default PrintSummaryStats;
